import React, { useState, useMemo } from "react";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { useMaterialUIController } from "context";
import { formatLocalDateTime } from "utils/dateFormatter";
import { useApiQuery } from "hooks/useApiQuery";

import GenericListScreen from "components/GenericListScreen/GenericListScreen";
import { Column } from "components/GenericListScreen/types";

export default function UserAgreementStatusScreen() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    // Filters
    const [termsTypeFilter, setTermsTypeFilter] = useState("");
    const [agreementStatusFilter, setAgreementStatusFilter] = useState("");

    // Load Terms Types
    const { data: termsTypesData } = useApiQuery("CommonCode.getByGroup", { groupCode: "TERMS_TYPE" });
    const termsTypes = termsTypesData || [];

    // Columns
    const columns: Column<any>[] = useMemo(() => [
        {
            Header: "사용자",
            accessor: "userName",
            width: "20%",
            Cell: ({ row }: any) => (
                <MDBox>
                    <MDTypography variant="body2" fontWeight="medium">
                        {row.original.userName || "알 수 없음"}
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary">
                        {row.original.email || "-"}
                    </MDTypography>
                </MDBox>
            ),
        },
        {
            Header: "약관 유형",
            accessor: "termsType",
            width: "20%",
            Cell: ({ value }: any) => {
                const type = termsTypes.find((t: any) => t.code === value);
                return (
                    <MDTypography variant="body2">
                        {type ? type.name : value}
                    </MDTypography>
                );
            },
        },
        {
            Header: "버전",
            accessor: "version",
            width: "10%",
            Cell: ({ value }: any) => (
                <MDTypography variant="body2" fontWeight="medium">
                    {value}
                </MDTypography>
            ),
        },
        {
            Header: "동의 상태",
            accessor: "agreed",
            width: "10%",
            align: "center",
            Cell: ({ value }: any) => (
                <Chip
                    label={value ? "동의" : "거부"}
                    color={value ? "success" : "error"}
                    size="small"
                    sx={{
                        fontWeight: "medium",
                        ...(value && {
                            backgroundColor: "#4CAF50",
                            color: "#ffffff",
                            "& .MuiChip-label": { color: "#ffffff" },
                        }),
                    }}
                />
            ),
        },
        {
            Header: "동의일시",
            accessor: "agreedAt",
            width: "20%",
            Cell: ({ value }: any) => (
                <MDTypography variant="caption" color={value ? "text.secondary" : "error"}>
                    {value ? formatLocalDateTime(value) : "-"}
                </MDTypography>
            ),
        },
        {
            Header: "IP 주소",
            accessor: "ipAddress",
            width: "20%",
            Cell: ({ value }: any) => (
                <MDTypography variant="caption" color="text.secondary">
                    {value || "-"}
                </MDTypography>
            ),
        },
    ], [termsTypes, darkMode]);

    // Extra Params
    const extraParams = useMemo(() => ({
        TermsType: termsTypeFilter || undefined,
        Agreed: agreementStatusFilter === "AGREED" ? true : agreementStatusFilter === "WITHDRAWN" ? false : undefined,
    }), [termsTypeFilter, agreementStatusFilter]);

    // Action Component (Filters)
    const filterComponent = (
        <MDBox display="flex" gap={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel sx={{ color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>약관 유형</InputLabel>
                <Select
                    value={termsTypeFilter}
                    label="약관 유형"
                    onChange={(e) => setTermsTypeFilter(e.target.value)}
                    sx={{
                        height: '40px',
                        backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : '#fff',
                        color: darkMode ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: darkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                        },
                        '& .MuiSvgIcon-root': {
                            color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.54)',
                        },
                    }}
                >
                    <MenuItem value="">전체</MenuItem>
                    {termsTypes.map((type: any) => (
                        <MenuItem key={type.code} value={type.code}>
                            {type.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel sx={{ color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>동의 상태</InputLabel>
                <Select
                    value={agreementStatusFilter}
                    label="동의 상태"
                    onChange={(e) => setAgreementStatusFilter(e.target.value)}
                    sx={{
                        height: '40px',
                        backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : '#fff',
                        color: darkMode ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: darkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                        },
                        '& .MuiSvgIcon-root': {
                            color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.54)',
                        },
                    }}
                >
                    <MenuItem value="">전체</MenuItem>
                    <MenuItem value="AGREED">동의</MenuItem>
                    <MenuItem value="WITHDRAWN">거부</MenuItem>
                </Select>
            </FormControl>
        </MDBox>
    );

    return (
        <GenericListScreen
            title="사용자 동의 현황 ( USER AGREEMENT STATUS )"
            icon="fact_check"
            apiName="UserAgreement.pagedList"
            columns={columns}
            actionComponent={filterComponent}
            extraParams={extraParams}
        />
    );
}
